import { HeroPipeline } from "../components/HeroPipeline";
import { MotionReveal } from "../components/MotionReveal";
import { MetricCounter } from "../components/MetricCounter";
import { Link } from "react-router-dom";

export default function HomePage() {
  return (
    <div className="relative">
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="pt-10 sm:pt-14">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
                Mission Control for Enterprise Intelligence
              </div>
              <h1 className="mt-6 font-[var(--font-display)] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
                AIOps & Observability
                <span className="block gradient-text">Centre of Excellence</span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--text-secondary)]">
                Turning Logs, Metrics, Traces & Packets into predictive intelligence and automated action.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                >
                  Request AIOps Assessment
                </Link>
                <Link
                  to="/industries/government"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white border border-white/15 bg-white/5 hover:bg-white/8"
                >
                  Explore Industry Use Cases
                </Link>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <SignalChip label="Noise Reduction" value="60–80%" />
                <SignalChip label="MTTR Improvement" value="40–70%" />
                <SignalChip label="RCA Speed" value="90%" />
                <SignalChip label="Ops Efficiency" value="50%+" />
              </div>
            </div>

            <div className="pb-10 sm:pb-14">
              <div className="glass-panel rounded-3xl p-4 sm:p-6">
                <HeroPipeline />
              </div>
              <div className="mt-4 text-xs text-[var(--text-secondary)]">
                Signal flow: Logs → Metrics → Traces → Packets → AI Correlation → Decision → Automation
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionShell>
        <MotionReveal>
          <div className="grid gap-10 lg:grid-cols-[0.45fr_0.55fr] lg:items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
                Reality in large enterprises
              </div>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                The Observability Challenge
              </h2>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-[var(--text-secondary)]">
                Modern operations produce massive telemetry volumes, but most organizations still operate with disconnected tools,
                fragmented teams, and reactive processes.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Card title="Tool Sprawl" desc="Average enterprise uses 10+ monitoring tools with no unified intelligence layer" />
              <Card title="Alert Fatigue" desc="Teams drowning in noise, missing critical signals" />
              <Card title="Manual Operations" desc="Skilled engineers wasting time on repetitive triage" />
              <Card title="Reactive Posture" desc="Finding problems after Customers complain" />
            </div>
          </div>
        </MotionReveal>
      </SectionShell>

      <SectionShell>
        <MotionReveal>
          <div className="grid gap-8">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
                Operating model
              </div>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                From Chaos to Clarity
              </h2>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <ComparisonPanel
                title="Without CoE"
                items={["Tool silos", "Alert storms", "Manual runbooks", "Reactive firefighting"]}
                accent="rgba(245,158,11,0.45)"
              />
              <ComparisonPanel
                title="With CoE"
                items={["Unified intelligence", "Correlated insights", "Automated remediation", "Predictive prevention"]}
                accent="rgba(16,185,129,0.45)"
              />
            </div>
          </div>
        </MotionReveal>
      </SectionShell>

      <SectionShell>
        <MotionReveal>
          <div className="grid gap-8">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
                Best-of-breed
              </div>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                Best-of-Breed Technology Stack
              </h2>
              <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
                Best tool for each signal. Unified intelligence at the top.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <LogoTile name="Dynatrace" />
              <LogoTile name="Splunk" />
              <LogoTile name="Datadog" />
              <LogoTile name="Elastic" />
              <LogoTile name="NETSCOUT" />
              <LogoTile name="Keysight" />
              <LogoTile name="Zabbix" />
              <LogoTile name="OpenTelemetry" ghost />
            </div>
          </div>
        </MotionReveal>
      </SectionShell>

      <SectionShell>
        <MotionReveal>
          <div className="grid gap-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
                  Impact by sector
                </div>
                <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                  Industries We Serve
                </h2>
              </div>
              <Link to="/industries/government" className="text-sm text-[var(--accent-primary)] hover:brightness-110">
                View all use cases
              </Link>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <IndustryCard
                title="Government"
                tagline="Reliable public services through intelligent operations"
                href="/industries/government"
              />
              <IndustryCard title="BFSI" tagline="Digital banking with zero-compromise reliability" href="/industries/bfsi" />
              <IndustryCard
                title="Digital-Native"
                tagline="Scale fast. Stay reliable."
                href="/industries/digital-native"
              />
              <IndustryCard title="Defence" tagline="Mission assurance through operational resilience" href="/industries/defence" />
            </div>
          </div>
        </MotionReveal>
      </SectionShell>

      <SectionShell>
        <MotionReveal>
          <div className="grid gap-8">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Executive metrics</div>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                Metrics That Matter
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard title="Alert Reduction" value={80} suffix="%" range="60–80%" />
              <MetricCard title="MTTR Improvement" value={70} suffix="%" range="40–70%" />
              <MetricCard title="Faster Root Cause" value={90} suffix="%" range="90%" />
              <MetricCard title="Ops Efficiency Gains" value={50} suffix="%+" range="50%+" />
            </div>
          </div>
        </MotionReveal>
      </SectionShell>

      <SectionShell>
        <MotionReveal>
          <div className="glass-panel rounded-3xl px-6 py-10 sm:px-10">
            <div className="grid gap-8 lg:grid-cols-[1.25fr_0.75fr] lg:items-center">
              <div>
                <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                  Build Your Intelligent Operations Nervous System
                </h2>
                <p className="mt-4 max-w-2xl text-sm sm:text-base text-[var(--text-secondary)]">
                  Every enterprise needs a central nervous system for IT operations. Let us help you build yours.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                >
                  Schedule Executive Workshop
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white border border-white/15 bg-white/5 hover:bg-white/8"
                >
                  Start AIOps & ROI Assessment
                </a>
              </div>
            </div>
          </div>
        </MotionReveal>
      </SectionShell>
    </div>
  );
}

function SectionShell({ children }) {
  return (
    <section className="mt-16 sm:mt-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

function Card({ title, desc }) {
  return (
    <div className="glass-panel hover-lift rounded-3xl p-6">
      <div className="font-[var(--font-display)] text-lg tracking-tight text-white">{title}</div>
      <div className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">{desc}</div>
    </div>
  );
}

function ComparisonPanel({ title, items, accent }) {
  return (
    <div className="glass-panel rounded-3xl p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="font-[var(--font-display)] text-lg tracking-tight text-white">{title}</div>
        <span className="h-2 w-16 rounded-full" style={{ background: accent }} />
      </div>
      <div className="mt-4 grid gap-3">
        {items.map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/3 px-4 py-3">
            <span className="h-2 w-2 rounded-full bg-[var(--accent-primary)] opacity-80" />
            <span className="text-sm text-[var(--text-secondary)]">{i}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogoTile({ name, ghost }) {
  return (
    <div
      className={`rounded-3xl border border-white/10 bg-white/3 px-5 py-6 text-center ${
        ghost ? "opacity-70" : ""
      }`}
    >
      <div className="font-[var(--font-mono)] text-xs tracking-[0.2em] text-[var(--text-secondary)]">PARTNER</div>
      <div className="mt-3 font-[var(--font-display)] text-xl text-white">{name}</div>
    </div>
  );
}

function IndustryCard({ title, tagline, href }) {
  return (
    <Link to={href} className="glass-panel hover-lift block rounded-3xl p-7">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-[var(--font-display)] text-xl tracking-tight text-white">{title}</div>
          <div className="mt-2 text-sm text-[var(--text-secondary)]">{tagline}</div>
        </div>
        <div
          className="h-10 w-10 rounded-2xl border border-white/10 bg-white/5"
          style={{ boxShadow: "0 0 18px rgba(0,212,255,0.15)" }}
        />
      </div>
      <div className="mt-6 text-sm font-medium text-[var(--accent-primary)]">Explore use cases</div>
    </Link>
  );
}

function MetricCard({ title, value, suffix, range }) {
  return (
    <div className="glass-panel hover-lift rounded-3xl p-6">
      <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">{title}</div>
      <div className="mt-4 flex items-end justify-between gap-4">
        <div className="text-4xl text-white">
          <MetricCounter value={value} suffix={suffix} />
        </div>
        <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-[var(--text-secondary)]">
          {range}
        </div>
      </div>
      <div className="mt-5 h-[2px] w-full rounded-full bg-white/5">
        <div className="h-full w-2/3 rounded-full bg-[var(--accent-primary)] opacity-60" />
      </div>
    </div>
  );
}

function SignalChip({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/3 px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-secondary)]">{label}</div>
      <div className="mt-1 font-[var(--font-mono)] text-sm text-white">{value}</div>
    </div>
  );
}
